import React, { useState, useEffect } from 'react';
import { Phone, Send, X, Clock, MapPin, Briefcase, AlertCircle, Check, MessageSquare, Zap, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { BACKEND_URL } from '../config/api';

// Employer component to create urgent requests
export function UrgentRequestForm({ onClose, targetTrade, onSuccess, subscription }) {
  const [loading, setLoading] = useState(false);
  const [showSubscriptionRequired, setShowSubscriptionRequired] = useState(false);
  const [formData, setFormData] = useState({
    trade: targetTrade || '',
    location: '',
    description: '',
    urgency: 'urgent',
    budget_range: '',
    contact_preference: 'message'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check subscription before sending (subscription passed from parent)
    if (!subscription?.active) {
      setShowSubscriptionRequired(true);
      return;
    }
    
    if (!formData.trade || !formData.description) {
      toast.error('Please fill in trade and description');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/on-call/urgent-request`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      onSuccess?.(response.data);
      onClose?.();
    } catch (error) {
      if (error.response?.status === 402) {
        // Subscription required
        setShowSubscriptionRequired(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send request');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show subscription required modal
  if (showSubscriptionRequired) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Zap className="h-8 w-8 text-orange-500" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Subscription Required</h3>
        <p className="text-slate-600 mb-6 max-w-sm mx-auto">
          Upgrade to send urgent requests and get instant notifications to available tradespeople.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => setShowSubscriptionRequired(false)}
            className="px-4 py-2 text-slate-600 hover:text-slate-800"
          >
            Go Back
          </button>
          <button
            onClick={() => {
              onClose?.();
              window.location.href = '/';
            }}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2">
        <Zap className="h-5 w-5 text-emerald-600 mt-0.5" />
        <div className="text-sm text-emerald-800">
          <strong>On Call Alert:</strong> This will notify all On Call tradespeople matching your criteria.
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Trade Required *
        </label>
        <input
          type="text"
          value={formData.trade}
          onChange={(e) => setFormData({ ...formData, trade: e.target.value })}
          placeholder="e.g., Plumber, Electrician"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          required
          data-testid="urgent-request-trade"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Location
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="e.g., Manchester, London"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          data-testid="urgent-request-location"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Job Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe the urgent work needed..."
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          required
          data-testid="urgent-request-description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Urgency Level
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'urgent', label: 'URGENT', color: 'red' },
            { value: 'same_day', label: 'Same Day', color: 'orange' },
            { value: 'next_day', label: 'Next Day', color: 'yellow' }
          ].map(({ value, label, color }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFormData({ ...formData, urgency: value })}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                formData.urgency === value
                  ? color === 'red' 
                    ? 'bg-red-500 text-white border-red-500' 
                    : color === 'orange'
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-yellow-500 text-white border-yellow-500'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Budget Range (optional)
        </label>
        <select
          value={formData.budget_range}
          onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          data-testid="urgent-request-budget"
        >
          <option value="">Select budget range</option>
          <option value="Under £50">Under £50</option>
          <option value="£50-100">£50-100</option>
          <option value="£100-200">£100-200</option>
          <option value="£200-500">£200-500</option>
          <option value="£500+">£500+</option>
          <option value="Quote needed">Quote needed</option>
        </select>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 text-slate-700 font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          data-testid="urgent-request-submit"
        >
          {loading ? (
            <span className="animate-pulse">Sending...</span>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Send Alert
            </>
          )}
        </button>
      </div>
    </form>
  );
}


// Tradesperson component to view and respond to urgent requests
export function UrgentRequestsList({ onRequestClick }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
    // Poll for new requests every 30 seconds
    const interval = setInterval(fetchRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/on-call/urgent-requests`, {
        withCredentials: true
      });
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch urgent requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-20 bg-slate-200 rounded-lg"></div>
        <div className="h-20 bg-slate-200 rounded-lg"></div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Phone className="h-10 w-10 mx-auto mb-2 opacity-50" />
        <p>No urgent requests at the moment</p>
        <p className="text-sm">Stay On Call to receive alerts!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="urgent-requests-list">
      {requests.map((request) => (
        <div
          key={request.request_id}
          onClick={() => onRequestClick?.(request)}
          className="bg-white border-2 border-emerald-200 rounded-xl p-4 cursor-pointer hover:border-emerald-400 transition-all"
          data-testid={`urgent-request-${request.request_id}`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                request.urgency === 'urgent' 
                  ? 'bg-red-100 text-red-700' 
                  : request.urgency === 'same_day'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {request.urgency === 'urgent' ? 'URGENT' : request.urgency === 'same_day' ? 'Same Day' : 'Next Day'}
              </span>
              {request.target_tradesperson_id && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                  Direct Request
                </span>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </div>
          
          <h4 className="font-bold text-slate-900 mb-1">{request.trade}</h4>
          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{request.description}</p>
          
          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            {request.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {request.location}
              </span>
            )}
            {request.budget_range && (
              <span className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                {request.budget_range}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(request.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}


// Modal for responding to urgent request
export function UrgentRequestResponseModal({ request, isOpen, onClose, onRespond }) {
  const [responding, setResponding] = useState(false);
  const [responseType, setResponseType] = useState(null);
  const [counterNotes, setCounterNotes] = useState('');

  const handleRespond = async (type) => {
    setResponding(true);
    setResponseType(type);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/on-call/urgent-requests/${request.request_id}/respond`,
        {
          response_type: type,
          counter_notes: type === 'counter' ? counterNotes : null
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      onRespond?.(type);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setResponding(false);
      setResponseType(null);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-500" />
            Urgent Job Request
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Urgency badge */}
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-sm font-bold rounded-full ${
              request.urgency === 'urgent' 
                ? 'bg-red-100 text-red-700' 
                : request.urgency === 'same_day'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {request.urgency === 'urgent' ? 'URGENT' : request.urgency === 'same_day' ? 'Same Day' : 'Next Day'}
            </span>
            {request.target_tradesperson_id && (
              <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-700 rounded-full">
                Direct Request to You
              </span>
            )}
          </div>

          {/* Job details */}
          <div className="space-y-3">
            <div>
              <p className="text-sm text-slate-500">Trade Required</p>
              <p className="font-bold text-slate-900">{request.trade}</p>
            </div>
            
            <div>
              <p className="text-sm text-slate-500">Job Description</p>
              <p className="text-slate-700">{request.description}</p>
            </div>
            
            {request.location && (
              <div className="flex items-center gap-2 text-slate-700">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span>{request.location}</span>
              </div>
            )}
            
            {request.budget_range && (
              <div className="flex items-center gap-2 text-slate-700">
                <Briefcase className="h-4 w-4 text-slate-400" />
                <span>Budget: {request.budget_range}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Clock className="h-4 w-4" />
              <span>Posted {new Date(request.created_at).toLocaleString()}</span>
            </div>
            
            <div className="text-sm text-slate-600">
              <strong>From:</strong> {request.employer_name}
            </div>
          </div>

          {/* Response options */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">How would you like to respond?</p>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleRespond('accepted')}
                disabled={responding}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="respond-accept"
              >
                {responding && responseType === 'accepted' ? (
                  <span className="animate-pulse">Sending...</span>
                ) : (
                  <>
                    <Check className="h-5 w-5" />
                    Accept
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleRespond('declined')}
                disabled={responding}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50"
                data-testid="respond-decline"
              >
                {responding && responseType === 'declined' ? (
                  <span className="animate-pulse">Sending...</span>
                ) : (
                  <>
                    <X className="h-5 w-5" />
                    Decline
                  </>
                )}
              </button>
            </div>

            {/* Counter offer option */}
            <div className="mt-4">
              <button
                onClick={() => setResponseType('counter')}
                className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Send a counter proposal instead
              </button>
              
              {responseType === 'counter' && (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={counterNotes}
                    onChange={(e) => setCounterNotes(e.target.value)}
                    placeholder="Describe your counter proposal (e.g., different time, adjusted price)..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    data-testid="counter-notes"
                  />
                  <button
                    onClick={() => handleRespond('counter')}
                    disabled={responding || !counterNotes}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50"
                    data-testid="respond-counter"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Send Counter Proposal
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Combined widget for tradesperson dashboard
export default function OnCallUrgentRequests() {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setShowResponseModal(true);
  };

  const handleRespond = () => {
    // Refresh list after responding
    setSelectedRequest(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="on-call-urgent-requests-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-emerald-100 rounded-lg">
          <Zap className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900">Urgent Requests</h3>
          <p className="text-sm text-slate-500">Jobs from employers needing immediate help</p>
        </div>
      </div>

      <UrgentRequestsList onRequestClick={handleRequestClick} />

      <UrgentRequestResponseModal
        request={selectedRequest}
        isOpen={showResponseModal}
        onClose={() => {
          setShowResponseModal(false);
          setSelectedRequest(null);
        }}
        onRespond={handleRespond}
      />
    </div>
  );
}
